import { BlockScrollStrategy, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Injector, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';

import { ActivatedRoute, Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { interval, Subject } from 'rxjs';

import {
  FEATURE_ANNOUNCEMENT_VERSION,
  ROUTER_LINK_PAGE_NAME_MAP,
  PORTAL_RELEASE_VERSION,
  DEFAULT_TABLE_PAGESIZE,
  AWS_COGNITO_REGION_KEY,
} from '@app-core/constants/constants';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { UpdateUserPermission } from '@app-shared/actions/user-permission.actions';
import { State } from '@app-shared/reducers';
import { CLIENT_CONFIG } from '@config/config';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '@app-auth/services/authentication.service';
import { CookieService } from '@app-core/services/cookie/cookie.service';
import { CommandbarService } from '@app-core/services/commandbar/commandbar.service';
import { CommandBarData } from '@app-core/models/core.model';
import { API } from '@app-core/constants/api.constants';
import { UtilityService } from '@app-shared/services/common/utility.service';
import { StartupService } from '@app-core/services/startup/startup.service';
import { environment } from 'src/environments/environment';
import { SuprsendService } from '@app-shared/components/suprsend-notification/suprsend.service';

export function scrollFactory(overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

/**
 * App component
 * TODO: Look at clearLocalIfUsingSession function in access service.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [{ provide: MAT_SELECT_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] }],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private config = CLIENT_CONFIG;
  private authRoutes = CLIENT_CONFIG.authRoutes;
  private defaultRedirectUrl = 'home';
  private defaultMobileRedirectUrl = 'safety-events';
  public loading = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public loginUserId: string = '';
  public removeSideNav = true;
  public isMobile = false;

  @HostListener('window:logout')
  public logout() {
    this.logoutfromCompanionApp();
    this.commandbarService.closeCommandBar();
    this.dataService.userLogout.next();
  }

  constructor(
    private router: Router,
    private accessService: AccessService,
    private dataService: DataService,
    private gtmService: GoogleTagManagerService,
    private storageService: StorageService,
    private cdRef: ChangeDetectorRef,
    private store: Store<State>,
    public translate: TranslateService,
    private overlay: OverlayContainer,
    private auth: AuthenticationService,
    private cookieService: CookieService,
    private commandbarService: CommandbarService,
    private activatedRoute: ActivatedRoute,
    private utilService: UtilityService,
    private startupService: StartupService,
    private breakpointObserver: BreakpointObserver,
    private injector: Injector
  ) {}

  public async verifyLogin() {
    const { url: activeRoute = '' } = this.router || {};
    let loginStatus = this.isAuthenticUser();
    // Eg., "/trip-details?tripId=abc".split('/')[1] === 'trip-details'

    // 1st condition just checks the expires it return false event when userInfo does not exists
    // we should try refreshing the access token only when userInfo exists and 2nd condition checks that
    if (!loginStatus && Object.keys(this.accessService.getLoginInfo()).length) {
      await this.auth.refreshAccessToken();

      // resetting the login status
      loginStatus = this.isAuthenticUser();
    }
    const primaryPath = activeRoute.split('/')[1];
    if (!loginStatus && !this.authRoutes.includes(primaryPath)) {
      this.auth.redirectToLandingPage();
      return;
    }
  }

  public ngOnInit() {
    // to check if user is logged in from mobile app
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
    if (window.self != window.top) {
      window.addEventListener('message', (event) => {
        if (event?.data && event?.data?.eventType === 'navigation') {
          this.router.navigate([event.data.path]);
        }
      });
    }
    this.subscribeForUserLogin();
    this.subscribeForUserLogout();
    this.subscribeForNavigationChange();
    if (!Boolean(environment.isGeotabEnv)) {
      interval(30000)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.verifyLogin());
    }
    const region = this.storageService.getStorageValue(AWS_COGNITO_REGION_KEY);
    if (region) {
      API.refreshRegion(region);
    }
    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value === 'dark') {
        this.overlay.getContainerElement().classList.add('dark-theme');
      } else {
        this.overlay.getContainerElement().classList.remove('dark-theme');
      }
    });

    if (this.isAuthenticUser()) {
      const { customerName, loginName } = this.accessService.getLoginInfo();
      this.gtmService.setDataLayerVariables({
        fleetId: this.accessService.currentFleet,
        customerName,
      });
      this.triggerLMLogin({ loginName });
    }
    this.loginUserId = this.accessService.getLoginInfo().userId;

    this.dataService.getFleetDetails();

    // ===============================
    //   Developer Console (Frontend)
    // ===============================
    const w = window as any;
    const getVersionInfo = () => {
      const loginInfoData = this.accessService.getLoginInfo();
      return {
        version: typeof PORTAL_RELEASE_VERSION !== 'undefined' ? PORTAL_RELEASE_VERSION : 'unknown',
        environment: environment?.env || 'unknown',
        currentLanguage: this.accessService.currentLanguage || 'en',
        userType: loginInfoData?.userType || '',
        loginName: loginInfoData?.loginName || '',
      };
    };

    const banner = () => {
      console.log('%c🚀 Welcome to Developer Console!', 'font-size:16px; font-weight:bold; color:#0b7285;');
      console.log(
        '%cType %clm.help()%c to see available commands.',
        'color:#868e96;',
        'color:#0b7285; font-weight:bold;',
        'color:#868e96;'
      );
    };

    const lmAPI = {
      version(): string {
        return getVersionInfo().version;
      },
      environment(): string {
        return getVersionInfo().environment;
      },
      language(): string {
        return getVersionInfo().currentLanguage;
      },
      userType(): string {
        return getVersionInfo().userType;
      },
      loginName(): string {
        return getVersionInfo().loginName;
      },
      info(): Record<string, unknown> {
        const info = getVersionInfo();
        console.group('%cBuild Info', 'color:#0b7285; font-size:14px; font-weight:bold;');
        console.table(info);
        console.groupEnd();
        return info;
      },
      help(): void {
        console.group('%c📖 Console Commands', 'color:#0b7285; font-weight:bold; font-size:14px;');
        console.table([
          { Command: 'lm.version()', Description: 'Get current frontend version' },
          { Command: 'lm.environment()', Description: 'Get environment name' },
          { Command: 'lm.language()', Description: 'Get selected language' },
          { Command: 'lm.userType()', Description: 'Get logged-in user type' },
          { Command: 'lm.loginName()', Description: 'Get logged-in user name' },
          { Command: 'lm.info()', Description: 'Show all build info' },
          { Command: 'lm.help()', Description: 'List all available commands' },
        ]);
        console.groupEnd();
      },
    };

    if (!w.lm) {
      Object.defineProperty(w, 'lm', {
        value: Object.freeze({ ...lmAPI }),
        writable: true,
        configurable: true,
      });
      banner();
    } else {
      w.lm = Object.freeze({ ...w.lm, ...lmAPI });
      console.info('%cDeveloper Console: lm object redefined with latest API.', 'color:#0b7285; font-weight:bold;');
    }
    const companion_app_token = localStorage.getItem('companion-app-access-token');
    if (companion_app_token && companion_app_token.length > 0) {
      (window as any).handleRedirectionFromApp = (url: string, singleEvent: string) => {
        this.injector.get(SuprsendService).handleAppNotificationsRedirection(url, singleEvent);
      };
    }
  }

  public ngAfterViewInit() {
    this.dataService._currentCookiesConsent.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value && value.statisticalCookies === true) {
        this.gtmService.gtmConsent('granted', true);
      } else if (value?.statisticalCookies === false) {
        this.gtmService.gtmConsent('denied', false);
        this.cookieService.deleteAllCookies();
        this.commandbarService.closeCommandBar();
      }

      if (value && value?.functionalCookies === false) {
        this.dataService.deleteFunctionalCookies();
      }
    });
    if (this.loginUserId) {
      this.bootCommandBar();
    }
  }

  private sendDataToCommandBar(loginInfoData, otherData) {
    const data: CommandBarData = {
      userId: loginInfoData.userId,
      language: loginInfoData.userMetadata.language,
      theme: loginInfoData.userMetadata.theme,
      unit: loginInfoData.userMetadata.metricUnit,
      dateFormat: loginInfoData.userMetadata.dateFormat,
      portalVersion: PORTAL_RELEASE_VERSION,
      portalName: 'fleet_portal',
      userPermissions: otherData.permission,
    };

    this.commandbarService.setUserProperties(data);
  }

  private subscribeForNavigationChange() {
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: Event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loading = true;
          this.cdRef.detectChanges();
          break;
        }

        case event instanceof NavigationEnd: {
          const url = (event as NavigationEnd).url && (event as NavigationEnd).url.split('?')[0];
          this.removeSideNav = Boolean(this.activatedRoute?.root?.firstChild?.snapshot?.data?.removeSideNav) || false;
          const pageTitle = ROUTER_LINK_PAGE_NAME_MAP[url];
          if (pageTitle) {
            this.gtmService.customPageview(url, pageTitle);
            const routerFunc = (url) => history.pushState({}, '', url);
            window.CommandBar && window.CommandBar.addRouter(routerFunc);
          }
        }
        /* falls through */
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loading = false;
          this.cdRef.detectChanges();
          break;
        }

        default:
          break;
      }
    });
  }

  private subscribeForUserLogin() {
    this.dataService.userLogin.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async (data) => {
      const { loginInfo, redirectUrl = this.defaultRedirectUrl, loginType, directLogin = false } = data || {};
      if (loginInfo) {
        const { userMetadata, fleets = [], customerName = '' } = loginInfo;
        const {
          dateFormat = 'MM/DD/YYYY HH:mm:ss',
          isReadableDateFormat = true,
          timezone = 'Local',
          metricUnit = 'Miles',
          preferredFleet = '',
          language: userLanguage = 'en',
          theme = 'light',
          cookieConsent = null,
          incidentsTablePageSize = DEFAULT_TABLE_PAGESIZE,
          pinnedReports = [],
        } = userMetadata;

        const isFleetPresent = fleets.some((fl) => fl.fleetId === preferredFleet);
        const currentUserFleetId = isFleetPresent ? preferredFleet : fleets[0].fleetId;
        const currentFleetInfo = fleets.find((f) => f.fleetId === currentUserFleetId);

        this.accessService.currentTimeZone = timezone;
        this.dataService.currentTimeZone = timezone;

        this.accessService.currentMetricUnit = metricUnit;
        this.dataService.currentMetricUnit = metricUnit;

        if (Boolean(environment.isGeotabEnv)) this.accessService.currentDateFormat = dateFormat;
        else this.accessService.currentDateFormat = dateFormat.includes('HH') ? dateFormat : `${dateFormat} HH:mm:ss`;
        this.dataService.currentDateFormat = this.accessService.currentDateFormat;

        this.accessService.isReadableDateFormat = isReadableDateFormat;
        this.dataService.isReadableDateFormat = isReadableDateFormat;

        this.accessService.currentFleet = currentUserFleetId;
        this.dataService.currentFleet = currentUserFleetId;

        this.accessService.currentTheme = theme;
        this.dataService.currentTheme = theme;

        this.accessService.currentCookiesConsent = cookieConsent;
        this.dataService.currentCookiesConsent = cookieConsent;

        this.accessService.currentTablePageSize = incidentsTablePageSize;
        this.dataService.currentTablePageSize = incidentsTablePageSize;

        this.accessService.currentPinnedReports = pinnedReports;
        this.dataService.currentPinnedReports = pinnedReports;

        let pageLanguage;

        if (this.config?.showCustomLoginPage && directLogin) {
          pageLanguage = this.accessService.landingPageLanguage || userLanguage;
          if (pageLanguage !== userLanguage) {
            this.utilService.updateUserMetadata({
              language: pageLanguage,
            });
          }
        } else {
          pageLanguage = userLanguage;
        }

        this.accessService.currentLanguage = pageLanguage;
        this.dataService.currentLanguage = pageLanguage;

        this.store.dispatch(
          new UpdateUserPermission({
            currentFleet: currentUserFleetId,
            permissions: currentFleetInfo?.role?.permissions,
            uiConfigurations: currentFleetInfo?.role?.uiPermissions,
          })
        );

        this.accessService.setLoginInfo(loginInfo);
        this.gtmService.login({
          fleetId: currentUserFleetId,
          customerName,
          loginType,
        });
      }
      if (this.isAuthenticUser()) {
        if (this.isMobile) {
          this.redirectMobileLoggedInUser(this.defaultMobileRedirectUrl);
        } else {
          this.redirectLoggedInUser(redirectUrl);
        }
        this.bootCommandBar();
        const { loginName } = this.accessService.getLoginInfo();
        this.triggerLMLogin({ loginName });
      }
    });
  }

  private subscribeForUserLogout() {
    this.dataService.userLogout.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.commandbarService.closeCommandBar();
      const companion_app_token = localStorage.getItem('companion-app-access-token');
      if (companion_app_token && companion_app_token.length > 0) {
        this.logoutfromCompanionApp();
      } else {
        this.storageService.clearAll();
        this.storageService.setStorageValue(FEATURE_ANNOUNCEMENT_VERSION, 'hide');
        this.dataService.resetFleetDriverList();
      }
    });
  }

  private isAuthenticUser(): boolean {
    return this.accessService.hasValidToken();
  }

  public redirectLoggedInUser(redirectUrl = this.defaultRedirectUrl) {
    if (Boolean(environment.isGeotabEnv) || CLIENT_CONFIG?.showCustomLoginPage) {
      this.router.navigate([redirectUrl], { replaceUrl: Boolean(environment.isGeotabEnv) });
    } else {
      window.location.href = redirectUrl;
    }
  }

  public redirectMobileLoggedInUser(redirectUrl = this.defaultMobileRedirectUrl) {
    this.router.navigate([redirectUrl]);
  }

  private bootCommandBar() {
    const loginInfoData = this.accessService.getLoginInfo();
    const permission = this.accessService.getUserPermissions();
    const otherData = {
      ...(permission ? { permission } : { permission: [] }),
    };
    this.commandbarService.bootCommandBar(loginInfoData.userId);
    this.sendDataToCommandBar(loginInfoData, otherData);
  }

  private triggerLMLogin({ loginName }) {
    const lm_login = new CustomEvent('lm_login', {
      bubbles: true,
      detail: { loginName },
    });
    window.dispatchEvent(lm_login);
  }

  private logoutfromCompanionApp() {
    const companion_app_platform = localStorage.getItem('companion-app-platform');
    const w = window as any;
    if (companion_app_platform === 'ios') {
      w?.webkit?.messageHandlers?.nativeLogout.postMessage('logout');
    } else if (companion_app_platform === 'android') {
      w?.Android?.nativeLogout('logout');
    }
  }

  public checkIfUserIsLoggedIn() {
    const companion_app_token = localStorage.getItem('companion-app-access-token');
    if ((companion_app_token && companion_app_token.length > 0) || this.accessService.hasValidToken()) {
      return true;
    }
    return false;
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.startupService.clearSubscriptions();
  }
}
